import { useParams } from "react-router-dom";
import HelpForm from "../components/HelpForm.tsx";
import { Band, Hero, Prose, Split, Wrap, sectionHead } from "../components/ui/index.ts";
import { bookBySlug } from "../data/books.ts";
import NotFound from "./NotFound.tsx";

/**
 * The page a book sends you to when a puzzle stops moving.
 *
 * This used to be the front page at a second address, scrolled to a section —
 * which meant the QR code in the front of a book pointed at the company's
 * pitch and then jumped past it, and the address could not be printed with any
 * confidence that what a reader landed on was about their problem. It is its
 * own page now, so `/help` and `/help?book=…&puzzle=…` are the same document
 * with and without a puzzle in it.
 *
 * That address is printed and cannot move. Everything else here can be
 * rearranged freely; the path cannot, because it is in books already sold.
 */
export default function Help() {
  const { slug } = useParams();
  const book = slug ? bookBySlug(slug) : undefined;

  /*
   * A slug that names no archived book is a 404, not a form. The address was
   * either mistyped or printed in a book whose puzzles are not up yet, and
   * quietly falling back to the generic page would leave the reader hunting
   * for the difference.
   */
  if (slug && (!book || !book.archived)) return <NotFound />;

  return (
    <>
      <Hero
        eyebrow="Puzzle help"
        title="Stuck?"
        lede={
          book
            ? `Tell us how far you've got in ${book.title}${book.subtitle ? `: ${book.subtitle}` : ""} and we'll give you the next step. Just the one you're missing, not the answer.`
            : "Tell us how far you've got and we'll give you the next step. Just the one you're missing, not the answer."
        }
        emblem="small"
      />

      <Band>
        <Wrap>
          <HelpForm book={book} />
        </Wrap>
      </Band>

      <Band variant="alt">
        <Wrap>
          <Split>
            <Prose>
              <h2 className={sectionHead}>We won't solve it for you</h2>
              <p>
                You mark the cells you're sure about, and we fill in the slashes those marks force —
                the ones you'd have written yourself anyway.
              </p>
            </Prose>
            <Prose>
              <h2 className={sectionHead}>Hints come in stages</h2>
              <p>
                First we point you at a clue. Ask again and we'll tell you what it pairs with, and
                only if you ask a third time do you get the mark itself. Ask once more and we'll
                show you the reasoning, so you can find the next one without us. If something's
                marked wrong we'll say so, and which block it's in.
              </p>
            </Prose>
          </Split>
        </Wrap>
      </Band>
    </>
  );
}
